import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    Wrench,
    Search,
    Calendar,
    Building2,
    Clock,
    AlertTriangle,
    CheckCircle,
    Download,
    ChevronDown,
    Plus,
    X,
    Trash2,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

// ── Types ────────────────────────────────────────────────────

interface Employee {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
}

interface CorrectionEntry {
    id: number;
    start_time: string;
    end_time: string;
    duration: number;
    comment: string | null;
    admin_user_id: number;
    status: string;
    first_name: string;
    last_name: string;
    email: string;
    department_id: number | null;
    department_title: string | null;
    task_type_id: number | null;
    task_title: string | null;
    task_billable: boolean;
    business_id: number | null;
    business_name: string | null;
    core_service_id: number | null;
    core_service_title: string | null;
    core_service_sku: string | null;
    time_card_id: number | null;
    time_card_week_end: string | null;
}

interface TaskType {
    id: number;
    title: string;
    billable: boolean;
}

interface CoreService {
    id: number;
    title: string;
    sku: string | null;
}

interface Business {
    id: number;
    name: string;
}

interface TimeCard {
    id: number;
    week_end: string;
}

interface CorrectionResult {
    entryId: number;
    previousStatus: string;
    newStatus: string;
    previousDuration: number;
    newDuration: number;
    utilisationUpdated?: boolean;
    departmentUpdates?: Array<{
        departmentId: number;
        billableDuration: number;
        totalDuration: number;
        utilisation: number;
    }>;
}

// ── Helpers ──────────────────────────────────────────────────

const secToHours = (sec: number) => Math.round((sec / 3600) * 100) / 100;
const hoursToSec = (h: number) => Math.round(h * 3600);

const formatDateInput = (d: Date): string => d.toISOString().split("T")[0];

const getDefaultDates = () => {
    const now = new Date();
    const end = formatDateInput(now);
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { start: formatDateInput(start), end };
};

const statusColors: Record<string, string> = {
    draft: "var(--status-warning, #f0ad4e)",
    submitted: "var(--accent-primary, #58a6ff)",
};

const statusBadgeClass: Record<string, string> = {
    draft: "inactive",
    submitted: "active",
};

// ── Table Styles ─────────────────────────────────────────────

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
};

// ── Main Component ──────────────────────────────────────────

const TimeEntryCorrection: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    // Data
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [entries, setEntries] = useState<CorrectionEntry[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [startDate, setStartDate] = useState(() => getDefaultDates().start);
    const [endDate, setEndDate] = useState(() => getDefaultDates().end);
    const [selectedEmployee, setSelectedEmployee] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [search, setSearch] = useState("");

    // Editing
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editStatus, setEditStatus] = useState("");
    const [editHours, setEditHours] = useState("");
    const [editDate, setEditDate] = useState("");
    const [editTaskTypeId, setEditTaskTypeId] = useState("");
    const [editBusinessId, setEditBusinessId] = useState("");
    const [editTimeCardId, setEditTimeCardId] = useState("");
    const [editCoreServiceId, setEditCoreServiceId] = useState("");
    const [saving, setSaving] = useState(false);
    const [lastResult, setLastResult] = useState<CorrectionResult | null>(null);

    // Bulk
    const [selectedRows, setSelectedRows] = useState<CorrectionEntry[]>([]);
    const [bulkStatus, setBulkStatus] = useState<string>("");
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkResult, setBulkResult] = useState<{ corrected: number; utilisationRecordsUpdated: number } | null>(null);
    const [toggleClearRows, setToggleClearRows] = useState(false);

    // New entry
    const [showAddForm, setShowAddForm] = useState(false);
    const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [coreServices, setCoreServices] = useState<CoreService[]>([]);
    const [newEntry, setNewEntry] = useState({
        employeeId: "",
        date: formatDateInput(new Date()),
        hours: "",
        status: "submitted",
        businessId: "",
        coreServiceId: "",
        comment: "",
    });
    const [addSaving, setAddSaving] = useState(false);
    const [addResult, setAddResult] = useState<{ entryId: number; status: string } | null>(null);

    // Time cards for editing
    const [timeCards, setTimeCards] = useState<TimeCard[]>([]);

    const getToken = async () => acquireTokenSafe(instance, accounts);

    // Fetch employees
    useEffect(() => {
        const fetch_ = async () => {
            const token = await getToken();
            if (!token) return;
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/employees`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch employees");
                setEmployees(await res.json());
            } catch (err: any) {
                console.error("Employee fetch error:", err);
            }
        };
        fetch_();
    }, [accounts]); // eslint-disable-line

    // Fetch task types, businesses, and core services when add form or edit mode is active
    useEffect(() => {
        if (!showAddForm && editingId === null) return;
        if (taskTypes.length > 0 && businesses.length > 0 && coreServices.length > 0) return;
        const fetchLookups = async () => {
            const token = await getToken();
            if (!token) return;
            try {
                const [ttRes, bizRes, csRes] = await Promise.all([
                    fetch(`${getApiBaseUrl()}/api/time-corrections/task-types`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${getApiBaseUrl()}/api/time-corrections/businesses`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${getApiBaseUrl()}/api/time-corrections/core-services`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                if (ttRes.ok) setTaskTypes(await ttRes.json());
                if (bizRes.ok) setBusinesses(await bizRes.json());
                if (csRes.ok) setCoreServices(await csRes.json());
            } catch (err) {
                console.error("Lookup fetch error:", err);
            }
        };
        fetchLookups();
    }, [showAddForm, editingId]); // eslint-disable-line

    // Search entries
    const fetchEntries = useCallback(async () => {
        const token = await getToken();
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ startDate, endDate });
            if (selectedEmployee !== "all") params.set("employeeId", selectedEmployee);
            if (statusFilter !== "all") params.set("status", statusFilter);
            const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/search?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to search entries");
            setEntries(await res.json());
            setSelectedRows([]);
            setToggleClearRows(t => !t);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, selectedEmployee, statusFilter]); // eslint-disable-line

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    // Apply single correction
    const applyCorrection = async (entryId: number, newStatus: string, newDurationSec: number, newDate?: string, newTaskTypeId?: number, newBusinessId?: number, newTimeCardId?: number, newCoreServiceId?: number) => {
        const token = await getToken();
        if (!token) return;
        setSaving(true);
        setLastResult(null);
        try {
            const payload: Record<string, any> = { newStatus, newDuration: newDurationSec };
            if (newDate) payload.newDate = newDate;
            if (newTaskTypeId !== undefined) payload.newTaskTypeId = newTaskTypeId;
            if (newBusinessId !== undefined) payload.newBusinessId = newBusinessId;
            if (newTimeCardId !== undefined) payload.newTimeCardId = newTimeCardId;
            if (newCoreServiceId !== undefined) payload.newCoreServiceId = newCoreServiceId;
            const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/${entryId}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Failed to apply correction");
            }
            const result: CorrectionResult = await res.json();
            setLastResult(result);
            setEditingId(null);
            // Refresh
            await fetchEntries();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Apply bulk correction
    const applyBulkCorrection = async () => {
        if (!bulkStatus || selectedRows.length === 0) return;
        const token = await getToken();
        if (!token) return;
        setBulkSaving(true);
        setBulkResult(null);
        try {
            const corrections = selectedRows.map(row => ({
                entryId: row.id,
                newStatus: bulkStatus,
            }));
            const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/bulk`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ corrections }),
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Failed to apply bulk corrections");
            }
            const result = await res.json();
            setBulkResult({ corrected: result.corrected, utilisationRecordsUpdated: result.utilisationRecordsUpdated });
            setBulkStatus("");
            await fetchEntries();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBulkSaving(false);
        }
    };

    // Delete entry
    const deleteEntry = async (entryId: number) => {
        if (!window.confirm('Are you sure you want to delete this time entry? This cannot be undone.')) return;
        const token = await getToken();
        if (!token) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/${entryId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Failed to delete entry");
            }
            await fetchEntries();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Create new entry
    const createEntry = async () => {
        if (!newEntry.employeeId || !newEntry.date || !newEntry.hours) {
            setError("Employee, date, and hours are required.");
            return;
        }
        const hrs = parseFloat(newEntry.hours);
        if (isNaN(hrs) || hrs <= 0) {
            setError("Hours must be a positive number.");
            return;
        }
        const token = await getToken();
        if (!token) return;
        setAddSaving(true);
        setError(null);
        try {
            const res = await fetch(`${getApiBaseUrl()}/api/time-corrections`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    employeeId: parseInt(newEntry.employeeId, 10),
                    date: newEntry.date,
                    durationSeconds: hoursToSec(hrs),
                    status: newEntry.status,
                    businessId: newEntry.businessId ? parseInt(newEntry.businessId, 10) : null,
                    coreServiceId: newEntry.coreServiceId ? parseInt(newEntry.coreServiceId, 10) : null,
                    comment: newEntry.comment || null,
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Failed to create entry");
            }
            const result = await res.json();
            setAddResult({ entryId: result.entryId, status: result.status });
            setNewEntry({ employeeId: "", date: formatDateInput(new Date()), hours: "", status: "submitted", businessId: "", coreServiceId: "", comment: "" });
            setShowAddForm(false);
            await fetchEntries();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setAddSaving(false);
        }
    };

    // Filtered data (local search)
    const filteredData = useMemo(() => {
        if (!search) return entries;
        const s = search.toLowerCase();
        return entries.filter(
            (e) =>
                `${e.first_name} ${e.last_name}`.toLowerCase().includes(s) ||
                (e.email || "").toLowerCase().includes(s) ||
                (e.task_title || "").toLowerCase().includes(s) ||
                (e.business_name || "").toLowerCase().includes(s) ||
                (e.core_service_title || "").toLowerCase().includes(s) ||
                (e.department_title || "").toLowerCase().includes(s)
        );
    }, [entries, search]);

    // Start editing
    const startEdit = (row: CorrectionEntry) => {
        setEditingId(row.id);
        setEditStatus(row.status);
        setEditHours(secToHours(row.duration).toFixed(2));
        setEditDate(new Date(row.start_time).toISOString().split("T")[0]);
        setEditTaskTypeId(row.task_type_id ? String(row.task_type_id) : "");
        setEditBusinessId(row.business_id ? String(row.business_id) : "");
        setEditTimeCardId(row.time_card_id ? String(row.time_card_id) : "");
        setEditCoreServiceId(row.core_service_id ? String(row.core_service_id) : "");
        setLastResult(null);
        // Fetch time cards for the employee
        (async () => {
            const token = await getToken();
            if (!token) return;
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/time-corrections/time-cards/${row.admin_user_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setTimeCards(await res.json());
            } catch (err) {
                console.error("Time card fetch error:", err);
            }
        })();
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditStatus("");
        setEditHours("");
        setEditDate("");
        setEditTaskTypeId("");
        setEditBusinessId("");
        setEditTimeCardId("");
        setEditCoreServiceId("");
        setTimeCards([]);
    };

    // Export
    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        const data = filteredData.map((e) => ({
            Name: `${e.first_name} ${e.last_name}`,
            Email: e.email,
            Department: e.department_title || "",
            Date: new Date(e.start_time).toLocaleDateString("en-IE"),
            Task: e.task_title || "",
            "Core Service": e.core_service_title || "",
            Client: e.business_name || "",
            Hours: secToHours(e.duration).toFixed(2),
            Billable: e.task_billable ? "Yes" : "No",
            Status: e.status,
            "Week End": e.time_card_week_end ? new Date(e.time_card_week_end).toLocaleDateString("en-IE") : "",
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Time Entries");
        XLSX.writeFile(wb, `time_corrections_${startDate}_${endDate}.xlsx`);
    };

    // Columns
    const columns: TableColumn<CorrectionEntry>[] = [
        {
            name: "Name",
            selector: (row) => `${row.first_name} ${row.last_name}`,
            sortable: true,
            grow: 1.5,
            cell: (row) => <span className="cell-primary">{row.first_name} {row.last_name}</span>,
        },
        {
            name: "Date",
            selector: (row) => row.start_time,
            sortable: true,
            width: "140px",
            cell: (row) =>
                editingId === row.id ? (
                    <input
                        type="date"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                        style={{
                            width: 125,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    />
                ) : (
                    <span className="cell-secondary">{new Date(row.start_time).toLocaleDateString("en-IE")}</span>
                ),
        },
        {
            name: "Task",
            selector: (row) => row.task_title || "",
            sortable: true,
            cell: (row) =>
                editingId === row.id ? (
                    <select
                        value={editTaskTypeId}
                        onChange={(e) => setEditTaskTypeId(e.target.value)}
                        style={{
                            maxWidth: 140,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    >
                        <option value="">— None —</option>
                        {taskTypes.map(tt => <option key={tt.id} value={String(tt.id)}>{tt.title}{tt.billable ? " (billable)" : ""}</option>)}
                    </select>
                ) : (
                    <span className="cell-secondary">{row.task_title || "—"}</span>
                ),
        },
        {
            name: "Client",
            selector: (row) => row.business_name || "",
            sortable: true,
            cell: (row) =>
                editingId === row.id ? (
                    <select
                        value={editBusinessId}
                        onChange={(e) => setEditBusinessId(e.target.value)}
                        style={{
                            maxWidth: 140,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    >
                        <option value="">— None —</option>
                        {businesses.map(b => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                    </select>
                ) : (
                    <span className="cell-secondary">{row.business_name || "—"}</span>
                ),
        },
        {
            name: "Core Service",
            selector: (row) => row.core_service_title || "",
            sortable: true,
            cell: (row) =>
                editingId === row.id ? (
                    <select
                        value={editCoreServiceId}
                        onChange={(e) => setEditCoreServiceId(e.target.value)}
                        style={{
                            maxWidth: 160,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    >
                        <option value="">— None —</option>
                        {coreServices.map(cs => <option key={cs.id} value={String(cs.id)}>{cs.title}{cs.sku ? ` (${cs.sku})` : ""}</option>)}
                    </select>
                ) : (
                    <span className="cell-secondary" title={row.core_service_sku || undefined}>
                        {row.core_service_title || "—"}
                    </span>
                ),
        },
        {
            name: "Hours",
            selector: (row) => row.duration,
            sortable: true,
            right: true,
            width: "90px",
            cell: (row) =>
                editingId === row.id ? (
                    <input
                        type="number"
                        step="0.25"
                        min="0"
                        value={editHours}
                        onChange={(e) => setEditHours(e.target.value)}
                        style={{
                            width: 70,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                            textAlign: "right",
                        }}
                    />
                ) : (
                    <span className="cell-value">{secToHours(row.duration).toFixed(2)}</span>
                ),
        },
        {
            name: "Billable",
            selector: (row) => (row.task_billable ? 1 : 0),
            sortable: true,
            width: "90px",
            cell: (row) => (
                <span className={`status-badge ${row.task_billable ? "active" : "inactive"}`}>
                    {row.task_billable ? "Yes" : "No"}
                </span>
            ),
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            width: "130px",
            cell: (row) =>
                editingId === row.id ? (
                    <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        style={{
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    >
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                    </select>
                ) : (
                    <span
                        style={{ fontWeight: 600, color: statusColors[row.status] || "var(--text-secondary)" }}
                    >
                        {row.status}
                    </span>
                ),
        },
        {
            name: "Time Card",
            selector: (row) => row.time_card_id || 0,
            sortable: true,
            width: "180px",
            cell: (row) =>
                editingId === row.id ? (
                    <select
                        value={editTimeCardId}
                        onChange={(e) => setEditTimeCardId(e.target.value)}
                        style={{
                            maxWidth: 165,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid var(--accent-primary)",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-primary)",
                            fontSize: 13,
                        }}
                    >
                        <option value="">— None —</option>
                        {timeCards.map(tc => (
                            <option key={tc.id} value={String(tc.id)}>
                                #{tc.id} — {tc.week_end ? new Date(tc.week_end).toLocaleDateString("en-IE") : "no date"}
                            </option>
                        ))}
                    </select>
                ) : row.time_card_id ? (
                    <span className="cell-secondary">
                        #{row.time_card_id}{row.time_card_week_end ? ` (${new Date(row.time_card_week_end).toLocaleDateString("en-IE")})` : ""}
                    </span>
                ) : (
                    <span className="cell-secondary" style={{ opacity: 0.5 }}>—</span>
                ),
        },
        {
            name: "Actions",
            width: "150px",
            cell: (row) =>
                editingId === row.id ? (
                    <div style={{ display: "flex", gap: 4 }}>
                        <button
                            className="download-btn"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            disabled={saving}
                            onClick={() => applyCorrection(
                                row.id,
                                editStatus,
                                hoursToSec(parseFloat(editHours) || 0),
                                editDate || undefined,
                                editTaskTypeId ? parseInt(editTaskTypeId, 10) : undefined,
                                editBusinessId ? parseInt(editBusinessId, 10) : undefined,
                                editTimeCardId ? parseInt(editTimeCardId, 10) : 0,
                                editCoreServiceId ? parseInt(editCoreServiceId, 10) : 0
                            )}
                        >
                            {saving ? "..." : "Save"}
                        </button>
                        <button
                            className="download-btn"
                            style={{ padding: "4px 10px", fontSize: 12, opacity: 0.7 }}
                            onClick={cancelEdit}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div style={{ display: "flex", gap: 4 }}>
                        <button
                            className="download-btn"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => startEdit(row)}
                        >
                            Edit
                        </button>
                        <button
                            className="download-btn"
                            style={{ padding: "4px 8px", fontSize: 12, color: "var(--accent-red, #f85149)" }}
                            onClick={() => deleteEntry(row.id)}
                            title="Delete entry"
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <Wrench size={20} />
                        <h1>Time Entry Corrections</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="download-btn" onClick={() => { setShowAddForm(f => !f); setAddResult(null); }} title="Add new time entry">
                            <Plus size={16} /> Add Entry
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {/* Add entry result */}
            {addResult && (
                <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: "rgba(92,184,92,0.1)", color: "var(--status-success, #5cb85c)", border: "1px solid var(--status-success, #5cb85c)", display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircle size={18} />
                    <span>New time entry #{addResult.entryId} created successfully (status: {addResult.status}).</span>
                    <button onClick={() => setAddResult(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 600 }}>×</button>
                </div>
            )}

            {/* Add Entry Form */}
            {showAddForm && (
                <div style={{ padding: 20, marginBottom: 20, borderRadius: 8, background: "var(--bg-tertiary)", border: "1px solid var(--accent-primary)", position: "relative" }}>
                    <button onClick={() => setShowAddForm(false)} style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}><X size={18} /></button>
                    <h3 style={{ margin: "0 0 16px", color: "var(--text-primary)", fontSize: 15 }}>
                        <Plus size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />Add New Time Entry
                    </h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Employee *</label>
                            <select className="filter-select" style={{ width: "100%" }} value={newEntry.employeeId} onChange={e => setNewEntry(s => ({ ...s, employeeId: e.target.value }))}>
                                <option value="">Select employee...</option>
                                {employees.map(emp => <option key={emp.id} value={String(emp.id)}>{emp.first_name} {emp.last_name} ({emp.email})</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Date *</label>
                            <input type="date" className="search-input" style={{ width: "100%" }} value={newEntry.date} onChange={e => setNewEntry(s => ({ ...s, date: e.target.value }))} />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Hours *</label>
                            <input type="number" step="0.25" min="0" className="search-input" style={{ width: "100%" }} placeholder="e.g. 2.5" value={newEntry.hours} onChange={e => setNewEntry(s => ({ ...s, hours: e.target.value }))} />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Status</label>
                            <select
                                className="filter-select"
                                style={{ width: "100%", opacity: 0.6, cursor: "not-allowed" }}
                                value={newEntry.status}
                                disabled
                                title="New entries are always created as Submitted"
                                onChange={e => setNewEntry(s => ({ ...s, status: e.target.value }))}
                            >
                                <option value="submitted">Submitted</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Client</label>
                            <select className="filter-select" style={{ width: "100%" }} value={newEntry.businessId} onChange={e => setNewEntry(s => ({ ...s, businessId: e.target.value }))}>
                                <option value="">None</option>
                                {businesses.map(b => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Core Service</label>
                            <select className="filter-select" style={{ width: "100%" }} value={newEntry.coreServiceId} onChange={e => setNewEntry(s => ({ ...s, coreServiceId: e.target.value }))}>
                                <option value="">None</option>
                                {coreServices.map(cs => <option key={cs.id} value={String(cs.id)}>{cs.title}{cs.sku ? ` (${cs.sku})` : ""}</option>)}
                            </select>
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Comment</label>
                            <input type="text" className="search-input" style={{ width: "100%" }} placeholder="Optional description..." value={newEntry.comment} onChange={e => setNewEntry(s => ({ ...s, comment: e.target.value }))} />
                        </div>
                    </div>
                    <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                        <button className="download-btn" onClick={createEntry} disabled={addSaving} style={{ padding: "8px 20px" }}>
                            {addSaving ? "Creating..." : "Create Entry"}
                        </button>
                        <button className="download-btn" onClick={() => setShowAddForm(false)} style={{ padding: "8px 20px", opacity: 0.7 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="table-controls" style={{ marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Calendar size={16} style={{ color: "var(--text-muted)" }} />
                    <input type="date" className="search-input" style={{ minWidth: 140 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <span style={{ color: "var(--text-muted)" }}>to</span>
                    <input type="date" className="search-input" style={{ minWidth: 140 }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Building2 size={16} style={{ color: "var(--text-muted)" }} />
                    <select className="filter-select" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
                        <option value="all">All Employees</option>
                        {employees.map((emp) => (
                            <option key={emp.id} value={String(emp.id)}>
                                {emp.first_name} {emp.last_name} ({emp.email})
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ChevronDown size={16} style={{ color: "var(--text-muted)" }} />
                    <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Statuses</option>
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Search size={16} style={{ color: "var(--text-muted)" }} />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search name, task, client..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: "rgba(217,83,79,0.1)", color: "var(--status-error, #d9534f)", border: "1px solid var(--status-error, #d9534f)" }}>
                    {error}
                    <button onClick={() => setError(null)} style={{ marginLeft: 12, background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 600 }}>×</button>
                </div>
            )}

            {/* Last correction result */}
            {lastResult && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 16,
                        borderRadius: 8,
                        background: "rgba(92,184,92,0.1)",
                        color: "var(--status-success, #5cb85c)",
                        border: "1px solid var(--status-success, #5cb85c)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <CheckCircle size={18} />
                    <span>
                        Entry #{lastResult.entryId} corrected: {lastResult.previousStatus} → {lastResult.newStatus},{" "}
                        {secToHours(lastResult.previousDuration).toFixed(2)}h → {secToHours(lastResult.newDuration).toFixed(2)}h.
                        {lastResult.utilisationUpdated && lastResult.departmentUpdates && lastResult.departmentUpdates.length > 0 && (
                            <> Utilisation reprocessed for {lastResult.departmentUpdates.length} department(s).</>
                        )}
                        {lastResult.utilisationUpdated === false && <> (Current week — utilisation not updated.)</>}
                    </span>
                    <button onClick={() => setLastResult(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 600 }}>×</button>
                </div>
            )}

            {/* Bulk result */}
            {bulkResult && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 16,
                        borderRadius: 8,
                        background: "rgba(92,184,92,0.1)",
                        color: "var(--status-success, #5cb85c)",
                        border: "1px solid var(--status-success, #5cb85c)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <CheckCircle size={18} />
                    <span>
                        Bulk correction complete: {bulkResult.corrected} entries updated, {bulkResult.utilisationRecordsUpdated} utilisation records reprocessed.
                    </span>
                    <button onClick={() => setBulkResult(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 600 }}>×</button>
                </div>
            )}

            {/* Summary Cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{filteredData.length}</div>
                        <div className="stat-label">Total Entries</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <AlertTriangle size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{filteredData.filter((e) => e.status === "draft").length}</div>
                        <div className="stat-label">Draft</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{filteredData.filter((e) => e.status === "submitted").length}</div>
                        <div className="stat-label">Submitted</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon purple">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">
                            {filteredData.reduce((sum, e) => sum + secToHours(e.duration), 0).toFixed(1)}
                        </div>
                        <div className="stat-label">Total Hours</div>
                    </div>
                </div>
            </div>

            {/* Bulk Actions */}
            {selectedRows.length > 0 && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 16,
                        borderRadius: 8,
                        background: "var(--bg-tertiary)",
                        border: "1px solid var(--border-color)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                        {selectedRows.length} selected
                    </span>
                    <span style={{ color: "var(--text-muted)" }}>Change status to:</span>
                    <select
                        className="filter-select"
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                        style={{ minWidth: 130 }}
                    >
                        <option value="">Select...</option>
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                    </select>
                    <button
                        className="download-btn"
                        disabled={!bulkStatus || bulkSaving}
                        onClick={applyBulkCorrection}
                        style={{ padding: "6px 16px" }}
                    >
                        {bulkSaving ? "Applying..." : `Apply to ${selectedRows.length} entries`}
                    </button>
                </div>
            )}

            {/* Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filteredData}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    selectableRows
                    onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                    clearSelectedRows={toggleClearRows}
                    defaultSortFieldId={2}
                    defaultSortAsc={false}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            No time entries found for the selected filters.
                        </div>
                    }
                />
            </div>

            {/* Info panel */}
            <div
                style={{
                    marginTop: 24,
                    padding: 16,
                    borderRadius: 8,
                    background: "var(--bg-tertiary)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                    lineHeight: 1.6,
                }}
            >
                <strong style={{ color: "var(--text-primary)" }}>How corrections work:</strong>
                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    <li>Click <strong>Add Entry</strong> to create a new time entry on behalf of any employee.</li>
                    <li>Click <strong>Edit</strong> on any row to change its status, hours, task type, client, or core service.</li>
                    <li><strong>Billable</strong> is driven by the linked <code>task_type</code>&rsquo;s <code>billable</code> flag — MinuteDock is no longer the source of truth.</li>
                    <li><strong>Core Service</strong> categorises the work (Accounting, Cloud, etc.) but does not affect billable determination.</li>
                    <li>Use checkboxes + the bulk action bar to change status for many entries at once.</li>
                    <li>For <strong>past weeks</strong>, saving a correction automatically reprocesses <code>department_utilisations</code> using a delta approach.</li>
                    <li><strong>Current week</strong> entries are updated but utilisation is not reprocessed (it is computed live).</li>
                    <li>Utilisation formula: <code>(billable_seconds / 3600) / target_hours × 100</code>.</li>
                </ul>
            </div>
        </div>
    );
};

export default TimeEntryCorrection;
